import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { ImageViewer } from "../../../components/ImageViewer";
import { CustomizeOptions } from "../../../components/CustomizeOptions";
import {
	Option,
	transformOptions,
	transformOriginalOptions,
} from "../../../utils/option";
import { useOptionData } from "../../../hook/useOptionData";
import { getModelByID } from "../../../apis/model";
import { Model, generateRandomId } from "../../../utils/model";
import { useGlobalSnackBar } from "../../SnackbarContext";
import { RootState } from "../../../store/store";
import { Loader } from "../../../components/Loader";
import { setQuoteAdminModels } from "../../../store/actions/adminAuthActions";

export const QuoteCustomization: React.FC = () => {
	const navigate = useNavigate();
	const { ID } = useParams();
	const { options, loading: optionLoading } = useOptionData();
	const { showSnackBar } = useGlobalSnackBar();

	const dispatch = useDispatch();

	const quoteAdminModels = useSelector(
		(state: RootState) => state.adminAuth.quoteAdminModels
	);

	const [orderedOptions, setOrderedOptions] = useState<Option[]>([]);
	const [categories, setCategories] = useState<Option[]>([]);
	const [model, setModel] = useState<Model>();
	const [isLoading, setLoading] = useState<boolean>(true);

	const IDs = useMemo(() => {
		return ID?.split("*");
	}, [ID]);

	useEffect(() => {
		const fetchModelData = async (ID: string) => {
			try {
				const modelData = await getModelByID({ ID });
				setModel({ ...modelData });
				setLoading(false);
			} catch (error) {
				showSnackBar("Failed to fetch model", "error");
			}
		};

		if (IDs) {
			fetchModelData(IDs[0]);
		} else {
			setLoading(false);
		}
	}, [IDs]);

	const makeOrderedOptionData = useCallback(() => {
		const orderedOptionData = transformOriginalOptions(
			transformOptions(options, true),
			true
		);
		const _orderedOptionData = orderedOptionData?.filter(
			(option: Option) => !option.is_category
		);
		const _orderedCategoryData = orderedOptionData?.filter(
			(option: Option) => option.is_category
		);

		if (
			IDs?.[1] &&
			quoteAdminModels.filter((res: Model) => res._id === IDs?.[1])?.length
		) {
			const choose_option = quoteAdminModels.filter((res: Model) => res._id === IDs?.[1])?.[0]?.chose_options

			const chooseOptionMap = new Map<string, any>(
				choose_option.map((item: any) => [item.ID, item])
			);

			const updatedOptions = options.map((option) => {
				if (chooseOptionMap.has(option.ID)) {
					return {
						...option,
						isAdded: true,
						QTYNumber: chooseOptionMap.get(option.ID)?.QTYNumber ?? null,
					};
				}
				return {
					...option,
					isAdded: false,
				};
			});
			setOrderedOptions(updatedOptions);
		} else {
			setOrderedOptions(_orderedOptionData);
		}

		setCategories(_orderedCategoryData);
	}, [options, optionLoading, quoteAdminModels, IDs]);

	useEffect(() => {
		if (!optionLoading) {
			makeOrderedOptionData();
		}
	}, [options, optionLoading, quoteAdminModels, IDs]);

	const handleFinish = useCallback(() => {
		if (!orderedOptions.length) return;
		const choseOptions = orderedOptions.filter((option: any) => option.isAdded);

		const _quoteModel = {
			...model,
			chose_options: choseOptions.map((item: any) => ({
				ID: item.ID,
				MSRP: item.MSRP,
				dealer_price: item.dealer_price,
				QTYNumber: item.QTYNumber
			})),
		};

		let newQuoteModels = quoteAdminModels;

		if (
			IDs?.[1] &&
			newQuoteModels.filter((res: Model) => res._id === IDs?.[1])?.length
		) {
			// edit section
			newQuoteModels = newQuoteModels.map((res: Model) => {
				if (res._id === IDs[1]) {
					return { ...res, ..._quoteModel };
				}
				return res;
			});
		} else {
			// add section
			newQuoteModels.push({ ..._quoteModel, _id: generateRandomId() });
		}

		dispatch(setQuoteAdminModels(newQuoteModels));
		if (
			IDs?.[1] &&
			newQuoteModels.filter((res: Model) => res._id === IDs?.[1])?.length
		) {
			window.history.back();
		} else {
			navigate(`/quote_builder`);
		}
	}, [model, orderedOptions]);

	if (optionLoading || isLoading) return <Loader isLoading={isLoading} />;

	return (
		<Box sx={{ flexGrow: 1 }}>
			<Grid container sx={{ pr: { xl: "15%" }, pl: { xl: "15%" } }}>
				<Grid item xs={12} sx={{ mb: 3 }}>
					<Paper
						variant="outlined"
						sx={{ width: "100%", p: 2 }}
					>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={5}>
								<ImageViewer
									images={
										model?.images
											? JSON.parse(model.images)?.filter(
												(item: any) => item !== null
											)
											: []
									}
								/>
							</Grid>
							<Grid item xs={12} sm={7}>
								<Typography variant="h5" gutterBottom>
									{model?.model_name}
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									Part # {model?.part_number}
								</Typography>

								<Grid container spacing={1}>
									<Grid item xs={6}>
										<Typography variant="caption" color="text.secondary">
											MSRP
										</Typography>
										<Typography variant="body1" fontWeight={600}>
											${model?.MSRP?.toLocaleString() ?? "0"}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Typography variant="caption" color="text.secondary">
											{process.env.REACT_APP_DEALER_NAME} Price
										</Typography>
										<Typography variant="body1" fontWeight={600}>
											${model?.dealer_price?.toLocaleString() ?? "0"}
										</Typography>
									</Grid>
									<Grid item xs={6} sx={{ mt: 1 }}>
										<Typography variant="caption" color="text.secondary">
											Dimensions (H x W x D)
										</Typography>
										<Typography variant="body2">
											{model?.demention_height} x {model?.demention_width} x {model?.demention_depth}
										</Typography>
									</Grid>
									<Grid item xs={6} sx={{ mt: 1 }}>
										<Typography variant="caption" color="text.secondary">
											Rack Spaces
										</Typography>
										<Typography variant="body2">
											{model?.demention_rack_spaces ?? "N/A"}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Paper>
				</Grid>
			</Grid>

			<Grid
				container
				sx={{ pr: { xl: "15%" }, pl: { xl: "15%" }, display: "block" }}
			>
				{!optionLoading && orderedOptions && (
					<CustomizeOptions
						options={orderedOptions}
						categories={categories}
						setOrderedOptions={setOrderedOptions}
						makeOrderedOptionData={makeOrderedOptionData}
						handleFinish={handleFinish}
						activeQuoteModel={model}
					/>
				)}
			</Grid>
		</Box>
	);
};
